import React, { useState, useEffect } from 'react';
import { Label } from "flowbite-react";
import { HiCheck, HiX } from "react-icons/hi";
// @ts-ignore
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export interface StrategyMapMilestone {
    id?: string;
    _uid?: string;
    name: string;
    start_date: string;
    color: string;
}

interface SmapMilestoneMapperProps {
    onSave?: (data: StrategyMapMilestone) => void;
    onUpdate?: (data: StrategyMapMilestone) => void;
    onCancel?: () => void;
    defaultData?: StrategyMapMilestone | null;
    minDate?: string;
    maxDate?: string;
}

export const SmapMilestoneMapper: React.FC<SmapMilestoneMapperProps> = ({
    onSave,
    onUpdate,
    onCancel,
    defaultData,
    minDate,
    maxDate
}) => {
    const [name, setName] = useState(defaultData?.name || '');
    const [date, setDate] = useState<string>(defaultData?.start_date || '');
    const [color, setColor] = useState(defaultData?.color || '#3b82f6');

    useEffect(() => {
        if (defaultData) {
            setName(defaultData.name);
            setDate(defaultData.start_date);
            setColor(defaultData.color);
        }
    }, [defaultData]);

    const handleSave = () => {
        if (!name.trim() || !date) return;

        const milestoneData: StrategyMapMilestone = {
            ...defaultData,
            name: name.trim(),
            start_date: date,
            color: color
        };

        if (defaultData) {
            onUpdate?.(milestoneData);
        } else {
            onSave?.({
                ...milestoneData,
                _uid: Date.now().toString()
            });
        }

        // Reset
        if (!defaultData) {
            setName('');
            setDate('');
            setColor('#3b82f6');
        }
    };

    return (
      <div className="w-full p-4 rounded-xl bg-light-surfacePrimary dark:bg-secondary-dark border border-light-border-default dark:border-slate-700 animate-fade-in mx-auto mt-2">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-base font-bold text-light-text-primary dark:text-white">
            {defaultData ? "Edit Milestone" : "Add Milestone"}
          </h2>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Milestone Name *
            </Label>
            <input
              type="text"
              className="w-full py-2 px-3 rounded-lg border border-light-border-default dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-primary-light text-black dark:text-white"
              placeholder="e.g. Q1 Launch"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs font-semibold mb-1 block">Date *</Label>
            <DatePicker
              className="w-full py-2 px-3 rounded-lg border border-light-border-default dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-primary-light text-black dark:text-white"
              selected={date ? new Date(date) : null}
              onChange={(d: Date) => setDate(d?.toISOString() || "")}
              minDate={minDate ? new Date(minDate) : undefined}
              maxDate={maxDate ? new Date(maxDate) : undefined}
              placeholderText="Select date"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold mb-1 block">Color</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="w-10 h-10 rounded-md border-none p-0 cursor-pointer bg-transparent"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
              <span className="text-xs text-light-text-muted dark:text-gray-400 font-mono">
                {color.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-light-border-default dark:border-slate-700">
          <button
            className="px-3 py-1.5 rounded-lg bg-light-surfaceSecondary text-light-text-primary font-semibold hover:bg-light-interactive-hover dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600  flex items-center gap-1 border border-light-border-default dark:border-slate-600 transition-colors duration-200 text-[10px]"
            onClick={() => {
              onCancel?.();
              if (!defaultData) {
                setName("");
                setDate("");
                setColor("#3b82f6");
              }
            }}
          >
            <HiX size={16} /> Cancel
          </button>
          <button
            className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1 transition-colors duration-200 ${
              !name.trim() || !date
                ? "bg-light-surfaceSecondary text-light-text-muted cursor-not-allowed border border-light-border-default"
                : "bg-primary-light text-white hover:bg-primary-hover shadow-sm"
            }`}
            onClick={handleSave}
            disabled={!name.trim() || !date}
          >
            <HiCheck size={16} />{" "}
            {defaultData ? "Update Milestone" : "Add Milestone"}
          </button>
        </div>
      </div>
    );
};
